
import { useSupabaseClient } from '@/hooks/useSupabaseClient';
import { Analysis, AnalysisWithProcurement } from '@/types/analysis';
import { updateProcurementStatus } from './procurementService';

export const fetchCompletedAnalyses = async (supabase: ReturnType<typeof useSupabaseClient>) => {
  const { data, error } = await supabase
    .from('analyses')
    .select(`
      *,
      procurement:procurement_id (
        id, title, authority, created_at, status
      )
    `)
    .eq('status', 'Klar');
    
  if (error) throw error;
  
  return data as AnalysisWithProcurement[];
};

export const fetchInProgressAnalyses = async (supabase: ReturnType<typeof useSupabaseClient>) => {
  const { data, error } = await supabase
    .from('analyses')
    .select(`
      *,
      procurement:procurement_id (
        id, title, authority, created_at, status
      )
    `)
    .eq('status', 'Pågående');
    
  if (error) throw error;
  
  return data as AnalysisWithProcurement[];
};

export const fetchAnalysisProcurementIds = async (supabase: ReturnType<typeof useSupabaseClient>) => {
  const { data, error } = await supabase
    .from('analyses')
    .select('procurement_id');
    
  if (error) throw error;
  
  return (data || []).map(a => a.procurement_id);
};

export const createAnalysis = async (supabase: ReturnType<typeof useSupabaseClient>, procurementId: string) => {
  // Create a new analysis entry
  const { data, error } = await supabase
    .from('analyses')
    .insert([
      {
        procurement_id: procurementId,
        status: 'Pågående',
        progress: 0
      }
    ])
    .select();

  if (error) throw error;

  // Update the procurement status
  await updateProcurementStatus(supabase, procurementId, 'Pågående');
  
  return data?.[0] as Analysis;
};

export const updateAnalysisProgress = async (
  supabase: ReturnType<typeof useSupabaseClient>, 
  analysisId: string, 
  currentProgress: number | null
) => {
  const newProgress = (currentProgress || 0) + 25;
  let updateData: Partial<Analysis> = { progress: newProgress };
  
  // If progress reaches 100%, mark as complete and add sample data
  if (newProgress >= 100) {
    updateData = {
      progress: 100,
      status: 'Klar',
      score: Math.floor(Math.random() * 20) + 80, // 80-99
      opportunities: ['Hög', 'Medium', 'Mycket hög'][Math.floor(Math.random() * 3)],
      complexity: ['Hög', 'Medium', 'Låg'][Math.floor(Math.random() * 3)],
      competition: ['Hög', 'Medium', 'Låg'][Math.floor(Math.random() * 3)],
      recommendations: JSON.stringify([
        "Fokusera på miljöcertifieringar och hållbarhetsaspekter i anbudet",
        "Presentera detaljerad tidplan med tydliga milstolpar",
        "Framhäv tidigare erfarenhet av liknande projekt med offentliga aktörer"
      ])
    };
    
    // Also update the procurement status
    const { data: analysisData } = await supabase
      .from('analyses')
      .select('procurement_id')
      .eq('id', analysisId)
      .single();
      
    if (analysisData) {
      await updateProcurementStatus(supabase, analysisData.procurement_id, 'Analyserad');
    }
  }
  
  // Update the analysis
  const { error } = await supabase
    .from('analyses')
    .update(updateData)
    .eq('id', analysisId);
    
  if (error) throw error;
  
  return updateData;
};
